import React, { useState, useEffect, useCallback } from 'react';

function PmsGoLivePurge({ token, selectedProperty }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [selectedMode, setSelectedMode] = useState(null); // 'full' | 'bookings-only'

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [restoreRow, setRestoreRow] = useState(null);
  const [restoreConfirmText, setRestoreConfirmText] = useState('');
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreError, setRestoreError] = useState(null); // string or { blockers: [...] }

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadHistory = useCallback(async () => {
    if (!selectedProperty?.id) { setHistory([]); return; }
    setHistoryLoading(true);
    try {
      const res = await fetch('/api/v1/pms/purge-history', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setHistory(Array.isArray(data.rows) ? data.rows : []);
    } catch (_) { /* leave history as-is */ }
    finally { setHistoryLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const executePurge = async () => {
    if (!selectedProperty?.id) { setError('No property selected'); return; }
    if (confirmText !== 'GO LIVE') { setError('Please type GO LIVE to confirm'); return; }
    setBusy(true); setError(''); setResult(null);
    try {
      const res = await fetch('/api/v1/pms/go-live-purge', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ mode: selectedMode, confirm: true })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Purge failed');
      setResult(data);
      setConfirmText('');
      setSelectedMode(null);
      loadHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadBackup = async (row) => {
    try {
      const res = await fetch(`/api/v1/pms/purge-history/${row.purge_id}/download`, { headers: headers() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Download failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (row.backup_filename || `purge-backup-${row.purge_id}.json`).replace(/\.gz$/i, '');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const openRestore = (row) => {
    setRestoreRow(row);
    setRestoreConfirmText('');
    setRestoreError(null);
  };

  const executeRestore = async () => {
    if (!restoreRow) return;
    if (restoreConfirmText !== 'RESTORE') { setRestoreError('Please type RESTORE to confirm'); return; }
    setRestoreBusy(true); setRestoreError(null);
    try {
      const res = await fetch(`/api/v1/pms/purge-history/${restoreRow.purge_id}/restore`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ confirm: true })
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409 && Array.isArray(data.blockers)) {
          setRestoreError({ blockers: data.blockers });
        } else {
          setRestoreError(data.error || 'Restore failed');
        }
        return;
      }
      setRestoreRow(null);
      loadHistory();
    } catch (err) {
      setRestoreError(err.message);
    } finally {
      setRestoreBusy(false);
    }
  };

  const cardStyle = (mode) => ({
    border: `2px solid ${selectedMode === mode ? '#3b82f6' : '#e5e7eb'}`,
    borderRadius: 10,
    padding: 24,
    cursor: 'pointer',
    background: selectedMode === mode ? '#eff6ff' : '#fff',
    flex: 1,
    minWidth: 280,
    transition: 'all 0.15s'
  });

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: 24 }}>
      <h2 style={{ marginBottom: 4 }}>Go Live — Purge Test Data</h2>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Remove test data before going live. Invoice numbering will restart from 1. All rooms will be reset to VACANT / CLEAN.
      </p>

      {/* ── Mode selection cards ── */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 28 }}>
        <div style={cardStyle('full')} onClick={() => { if (!busy) { setSelectedMode('full'); setConfirmText(''); setResult(null); setError(''); } }}>
          <h3 style={{ margin: '0 0 8px', color: '#dc2626' }}>Option 1 — Full Purge</h3>
          <p style={{ margin: '0 0 12px', color: '#374151', fontSize: 14 }}>
            Wipes <strong>all transactional data</strong> — guests, bookings, stays, invoices, folios, corporate ledger, compliance logs, housekeeping logs, night audit logs.
          </p>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            <strong>Keeps:</strong> Rooms, Room Types, Rate Plans, Corporates, Travel Agents, Tax Policies, Compliance Policy, Users, Roles, Settings, CM Mappings
          </div>
        </div>

        <div style={cardStyle('bookings-only')} onClick={() => { if (!busy) { setSelectedMode('bookings-only'); setConfirmText(''); setResult(null); setError(''); } }}>
          <h3 style={{ margin: '0 0 8px', color: '#d97706' }}>Option 2 — Booking & Invoice Reset</h3>
          <p style={{ margin: '0 0 12px', color: '#374151', fontSize: 14 }}>
            Wipes <strong>only bookings and invoices</strong> — reservations, stays, folios, invoices, guest checkins, corporate ledger entries.
          </p>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            <strong>Additionally keeps:</strong> Guest Master records, Compliance Logs, Housekeeping Logs, Night Audit Logs
          </div>
        </div>
      </div>

      {/* ── Confirmation panel ── */}
      {selectedMode && !result && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <p style={{ margin: '0 0 12px', fontWeight: 600, color: '#991b1b' }}>
            WARNING: This action is irreversible. All {selectedMode === 'full' ? 'transactional' : 'booking & invoice'} data for property "{selectedProperty?.name || selectedProperty?.id}" will be permanently deleted.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 14, color: '#374151' }}>
              Type <strong>GO LIVE</strong> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              disabled={busy}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, width: 140, fontWeight: 600, letterSpacing: 1 }}
              placeholder="GO LIVE"
            />
            <button
              onClick={executePurge}
              disabled={busy || confirmText !== 'GO LIVE'}
              style={{
                padding: '8px 24px',
                background: confirmText === 'GO LIVE' ? '#dc2626' : '#9ca3af',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: confirmText === 'GO LIVE' && !busy ? 'pointer' : 'not-allowed',
                fontSize: 14
              }}
            >
              {busy ? 'Purging...' : selectedMode === 'full' ? 'Execute Full Purge' : 'Execute Booking Reset'}
            </button>
            <button
              onClick={() => { setSelectedMode(null); setConfirmText(''); setError(''); }}
              disabled={busy}
              style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div style={{ background: '#fef2f2', color: '#991b1b', padding: '10px 16px', borderRadius: 6, marginBottom: 16, border: '1px solid #fecaca' }}>
          {error}
        </div>
      )}

      {/* ── Result ── */}
      {result && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 20 }}>
          <h3 style={{ margin: '0 0 12px', color: '#166534' }}>Purge Complete — {result.mode === 'full' ? 'Full Purge' : 'Booking Reset'}</h3>
          <p style={{ margin: '0 0 12px', color: '#374151', fontSize: 14 }}>
            Invoice numbering has been reset. Next invoice will start from <strong>1</strong>. All rooms set to VACANT / CLEAN.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#dcfce7' }}>
                <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #bbf7d0' }}>Table / Action</th>
                <th style={{ textAlign: 'right', padding: '6px 10px', borderBottom: '1px solid #bbf7d0' }}>Rows Affected</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(result.counts || {}).map(([table, count]) => (
                <tr key={table}>
                  <td style={{ padding: '4px 10px', borderBottom: '1px solid #f0f0f0', fontFamily: 'monospace', fontSize: 12 }}>{table}</td>
                  <td style={{ padding: '4px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontWeight: count > 0 ? 600 : 400, color: count > 0 ? '#166534' : '#9ca3af' }}>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ margin: '16px 0 0', fontSize: 13, color: '#6b7280' }}>
            Recommended: Run Inventory Rebuild from the Availability screen to regenerate inventory data.
          </p>
        </div>
      )}

      {/* ── Purge History ── */}
      <div style={{ marginTop: 32 }}>
        <h3 style={{ color: '#0F2C4D', margin: '0 0 4px' }}>Purge History</h3>
        <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 12 }}>
          Every purge automatically backs up all affected data before deleting it. A purge can only be restored if nothing has happened for this property since.
        </p>
        {historyLoading ? (
          <div style={{ color: '#6b7280', fontSize: 13 }}>Loading...</div>
        ) : history.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>No purges recorded yet for this property.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#0F2C4D', color: '#fff' }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px' }}>Mode</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px' }}>Triggered By</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px' }}>Rows Purged</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px' }}>Restored?</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const totalRows = Object.values(h.row_counts || {}).reduce((a, b) => a + (Number(b) || 0), 0);
                  return (
                    <tr key={h.purge_id} style={{ borderBottom: '1px solid #edf2f7' }}>
                      <td style={{ padding: '6px 10px' }}>{new Date(h.created_at).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '6px 10px' }}>{h.mode === 'full' ? 'Full Purge' : 'Booking Reset'}</td>
                      <td style={{ padding: '6px 10px' }}>{h.triggered_by_name || '-'}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }}>{totalRows}</td>
                      <td style={{ padding: '6px 10px' }}>
                        {h.restored_at ? (
                          <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                            Restored {new Date(h.restored_at).toLocaleDateString('en-IN')} by {h.restored_by_name || '-'}
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: 11 }}>Not restored</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                        <button onClick={() => downloadBackup(h)} style={{ padding: '4px 10px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                          Download
                        </button>
                        {!h.restored_at && (
                          <button onClick={() => openRestore(h)} style={{ marginLeft: 8, padding: '4px 10px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            Restore
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Restore confirmation modal ── */}
      {restoreRow && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget && !restoreBusy) setRestoreRow(null); }}>
          <div style={{ width: 'min(500px,94vw)', background: '#fff', borderRadius: 10, padding: '1.3rem' }}>
            <h3 style={{ marginTop: 0, color: '#0F2C4D' }}>Restore Purge from {new Date(restoreRow.created_at).toLocaleString('en-IN')}</h3>
            <p style={{ color: '#991b1b', fontSize: 13 }}>
              This re-inserts all data removed by that purge. It will be blocked if any new bookings, invoices, or other activity exist for this property since.
            </p>
            {restoreError && (
              <div style={{ background: '#fef2f2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 12, border: '1px solid #fecaca', fontSize: 13 }}>
                {typeof restoreError === 'string' ? restoreError : (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Restore blocked — new activity found:</div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {restoreError.blockers.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </>
                )}
              </div>
            )}
            <label style={{ display: 'block', fontSize: 14, color: '#374151', marginBottom: 12 }}>
              Type <strong>RESTORE</strong> to confirm:
              <input
                type="text"
                value={restoreConfirmText}
                onChange={(e) => setRestoreConfirmText(e.target.value.toUpperCase())}
                disabled={restoreBusy}
                style={{ display: 'block', marginTop: 6, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, width: 160, fontWeight: 600, letterSpacing: 1 }}
                placeholder="RESTORE"
              />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={executeRestore}
                disabled={restoreBusy || restoreConfirmText !== 'RESTORE'}
                style={{
                  padding: '8px 20px',
                  background: restoreConfirmText === 'RESTORE' ? '#dc2626' : '#9ca3af',
                  color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600,
                  cursor: restoreConfirmText === 'RESTORE' && !restoreBusy ? 'pointer' : 'not-allowed', fontSize: 14
                }}
              >
                {restoreBusy ? 'Restoring...' : 'Confirm Restore'}
              </button>
              <button onClick={() => setRestoreRow(null)} disabled={restoreBusy}
                style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PmsGoLivePurge;
